/**
 * Схема "Сессия вручную" (KAN-601, подпись KAN-677): .session Telethon или текст StringSession,
 * JSON продавца файлом или текстом, multipart на import/.
 * Лимиты проверяются на клиенте: .session до 1 МБ, JSON до 64 КБ.
 */
import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ConnectCommonFields } from '@/components/telegram/ConnectCommonFields';
import { ConnectResultStep } from '@/components/telegram/ConnectResultStep';
import { ConnectSellerFields } from '@/components/telegram/ConnectSellerFields';
import { PurchaseCostField } from '@/components/telegram/PurchaseCostField';
import { ImportFlagsFields } from '@/components/telegram/ImportFlagsFields';
import {
  DEFAULT_COMMON,
  DEFAULT_IMPORT_FLAGS,
  buildImportFormData,
  describeApiError,
  resolveLabel,
  skipSpamCheckFromFlag,
  validateSellerJson,
  validateSellerJsonFileSize,
  validateSessionFile,
  validateCommonProxy,
  validatePurchaseCost,
  type CommonValues,
  type ImportFlags,
} from '@/lib/telegramConnect';
import { proxyToUrl } from '@/lib/telegramProxy';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { ImportResult, TelegramAccount } from '@/types/telegramAccount';

type Kind = 'file' | 'text';

interface Props {
  onConnected: (account: TelegramAccount) => void;
  onClose: () => void;
  /** KAN-648: поля видеозаписи аккаунта; состояние живёт в мастере. */
  videoFields?: ReactNode;
  /** KAN-752: выбранная кампания (оператор); пусто - не передаётся. */
  operatorId?: string;
}

function KindToggle({
  name,
  value,
  onChange,
  fileLabel,
  textLabel,
}: {
  name: string;
  value: Kind;
  onChange: (k: Kind) => void;
  fileLabel: string;
  textLabel: string;
}): JSX.Element {
  return (
    <div className="flex gap-4 text-sm">
      {(['file', 'text'] as const).map((k) => (
        <label key={k} className="flex items-center gap-1">
          <input
            type="radio"
            name={name}
            data-testid={`${name}-${k}`}
            checked={value === k}
            onChange={() => onChange(k)}
          />
          {k === 'file' ? fileLabel : textLabel}
        </label>
      ))}
    </div>
  );
}

export function ImportSessionForm({
  onConnected,
  onClose,
  videoFields,
  operatorId,
}: Props): JSX.Element {
  const [common, setCommon] = useState<CommonValues>(DEFAULT_COMMON);
  const [sessionKind, setSessionKind] = useState<Kind>('file');
  const [sessionFile, setSessionFile] = useState<File | null>(null);
  const [sessionString, setSessionString] = useState('');
  const [jsonKind, setJsonKind] = useState<Kind>('file');
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [jsonText, setJsonText] = useState('');
  // KAN-676: проверка @SpamBot и "был 2FA" (ImportFlagsFields)
  const [flags, setFlags] = useState<ImportFlags>(DEFAULT_IMPORT_FLAGS);
  // KAN-670: продавец - карточка из справочника (common.sellerId)
  const [fileError, setFileError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const importMutation = useMutation({
    mutationFn: () =>
      telegramAccountsApi.importAccount(
        buildImportFormData({
          sessionFile: sessionKind === 'file' ? sessionFile : null,
          sessionString: sessionKind === 'text' ? sessionString : '',
          sellerJsonFile: jsonKind === 'file' ? jsonFile : null,
          sellerJsonText: jsonKind === 'text' ? jsonText : '',
          ...resolveLabel(common),
          proxy: proxyToUrl(common.proxy),
          proxyId: common.proxyId,
          enabled: common.enabled,
          outreach: common.outreach,
          skipSpamCheck: skipSpamCheckFromFlag(flags.checkSpam),
          had2fa: flags.had2fa,
          sellerId: common.sellerId,
          purchaseCost: common.purchaseCost,
          operatorId,
        })
      ),
    onSuccess: (res) => {
      // Секреты больше не нужны
      setSessionString('');
      setJsonText('');
      setSessionFile(null);
      setJsonFile(null);
      setResult(res);
      onConnected(res.account);
    },
    onError: (err: unknown) => {
      setError(describeApiError(err, 'Не удалось импортировать сессию'));
    },
  });

  const pickSessionFile = (file: File | null) => {
    setSessionFile(null);
    setFileError(null);
    if (!file) return;
    const problem = validateSessionFile(file);
    if (problem) setFileError(problem);
    else setSessionFile(file);
  };

  const pickJsonFile = async (file: File | null) => {
    setJsonFile(null);
    setJsonError(null);
    if (!file) return;
    const sizeProblem = validateSellerJsonFileSize(file.size);
    if (sizeProblem) {
      setJsonError(sizeProblem);
      return;
    }
    const problem = validateSellerJson(await file.text(), file.size);
    if (problem) setJsonError(problem);
    else setJsonFile(file);
  };

  const changeJsonText = (text: string) => {
    setJsonText(text);
    setJsonError(text.trim() ? validateSellerJson(text) : null);
  };

  if (result) {
    return (
      <ConnectResultStep
        account={result.account}
        scheme="session"
        spam={result.spam}
        selfMessage={result.self_message}
        onDone={onClose}
      />
    );
  }

  const sessionReady =
    sessionKind === 'file' ? !!sessionFile : !!sessionString.trim();
  const jsonReady = jsonKind === 'file' ? !!jsonFile : !!jsonText.trim();
  // пустые поля прокси = без прокси, ошибка только у заполненных
  const proxyError = validateCommonProxy(common);
  const costError = validatePurchaseCost(common.purchaseCost);
  const canSubmit =
    sessionReady &&
    jsonReady &&
    !fileError &&
    !jsonError &&
    !proxyError &&
    !costError &&
    !importMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Сессия</Label>
        <KindToggle
          name="session-kind"
          value={sessionKind}
          onChange={(k) => {
            setSessionKind(k);
            setFileError(null);
          }}
          fileLabel="Файл .session"
          textLabel="Текст StringSession"
        />
        {sessionKind === 'file' ? (
          <Input
            type="file"
            accept=".session"
            data-testid="session-file-input"
            aria-label="Файл .session"
            onChange={(e) => pickSessionFile(e.target.files?.[0] ?? null)}
          />
        ) : (
          <Input
            type="password"
            autoComplete="off"
            data-testid="session-string-input"
            aria-label="StringSession"
            value={sessionString}
            onChange={(e) => setSessionString(e.target.value)}
          />
        )}
        {fileError && (
          <p className="text-xs text-red-600" data-testid="session-file-error">
            {fileError}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>JSON продавца (обязательно)</Label>
        <KindToggle
          name="json-kind"
          value={jsonKind}
          onChange={(k) => {
            setJsonKind(k);
            setJsonError(null);
          }}
          fileLabel="Файл .json"
          textLabel="Текст JSON"
        />
        {jsonKind === 'file' ? (
          <Input
            type="file"
            accept=".json,application/json"
            data-testid="seller-json-file-input"
            aria-label="Файл JSON продавца"
            onChange={(e) => pickJsonFile(e.target.files?.[0] ?? null)}
          />
        ) : (
          <Textarea
            autoComplete="off"
            spellCheck={false}
            data-testid="seller-json-text-input"
            aria-label="Текст JSON продавца"
            className="[-webkit-text-security:disc]"
            value={jsonText}
            onChange={(e) => changeJsonText(e.target.value)}
          />
        )}
        {jsonError && (
          <p className="text-xs text-red-600" data-testid="seller-json-error">
            {jsonError}
          </p>
        )}
      </div>

      <ConnectCommonFields
        values={common}
        proxyError={proxyError}
        disabled={importMutation.isPending}
        onChange={(patch) => setCommon((prev) => ({ ...prev, ...patch }))}
      />

      <ConnectSellerFields
        sellerId={common.sellerId}
        disabled={importMutation.isPending}
        onChange={(sellerId) => setCommon((prev) => ({ ...prev, sellerId }))}
      />

      <PurchaseCostField
        value={common.purchaseCost}
        disabled={importMutation.isPending}
        onChange={(purchaseCost) => setCommon((prev) => ({ ...prev, purchaseCost }))}
      />

      {videoFields}

      <ImportFlagsFields
        idPrefix="import"
        values={flags}
        disabled={importMutation.isPending}
        onChange={(patch) => setFlags((prev) => ({ ...prev, ...patch }))}
      />

      {error && (
        <Alert variant="destructive" data-testid="connect-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button
          data-testid="connect-import-button"
          disabled={!canSubmit}
          onClick={() => {
            setError(null);
            importMutation.mutate();
          }}
        >
          {importMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Импортировать
        </Button>
      </div>
    </div>
  );
}
